import MojitoCore from 'mojito/core';
import CricketScoreboard from 'presentation/components/scoreboard/cricket-scoreboard/index.js';
import PreplayScoreboard from 'presentation/components/scoreboard/preplay-scoreboard/index.js';
import OutrightScoreboard from 'presentation/components/scoreboard/outright-scoreboard/index.js';
import GenericInplayScoreboard from 'presentation/components/scoreboard/generic-inplay-scoreboard/index.js';
import PeriodBasedInplayScoreboard from 'presentation/components/scoreboard/period-based-inplay-scoreboard/index.js';
import SetBasedInplayScoreboard from 'presentation/components/scoreboard/set-based-inplay-scoreboard/index.js';
import FlexPane from 'presentation/components/flex-pane/index.js';

const uiStyle = MojitoCore.Presentation.uiStyle;
const UIViewImplementation = MojitoCore.Presentation.UIViewImplementation;

const SCOREBOARD_TYPE = {
    OUTRIGHT: 'OUTRIGHT',
    SET_BASED_INPLAY: 'SET_BASED_INPLAY',
    PERIOD_BASED_INPLAY: 'PERIOD_BASED_INPLAY',
    GENERIC_BASED_INPLAY: 'GENERIC_BASED_INPLAY',
    PREPLAY: 'PREPLAY',
    CRICKET: 'CRICKET',
};

const SPORT_IDS = {
    CRICKET: 'cricket',
};

const INPLAY_DESCRIPTION = {
    NOT_STARTED: '_not_started',
};

const scoreboardRegistryByType = {
    [SCOREBOARD_TYPE.OUTRIGHT]: {
        name: 'outrightScoreboard',
        component: OutrightScoreboard,
    },
    [SCOREBOARD_TYPE.SET_BASED_INPLAY]: {
        name: 'setBasedInplayScoreboard',
        component: SetBasedInplayScoreboard,
    },
    [SCOREBOARD_TYPE.PERIOD_BASED_INPLAY]: {
        name: 'periodBasedInplayScoreboard',
        component: PeriodBasedInplayScoreboard,
    },
    [SCOREBOARD_TYPE.GENERIC_BASED_INPLAY]: {
        name: 'genericInplayScoreboard',
        component: GenericInplayScoreboard,
    },
    [SCOREBOARD_TYPE.CRICKET]: {
        name: 'cricketScoreboard',
        component: CricketScoreboard,
    },
    [SCOREBOARD_TYPE.PREPLAY]: {
        name: 'preplayScoreboard',
        component: PreplayScoreboard,
    },
};

export default class Scoreboard extends UIViewImplementation {
    getTypeOfScoreboard() {
        const { event, isOutright } = this.props;
        if (isOutright) {
            return SCOREBOARD_TYPE.OUTRIGHT;
        }
        if (
            !event.details ||
            !event.details.inPlay ||
            !event.details.detailedScores ||
            !event.details.inplayDescription ||
            event.details.inplayDescription === INPLAY_DESCRIPTION.NOT_STARTED
        ) {
            return SCOREBOARD_TYPE.PREPLAY;
        }
        if (this.config.scoreboard.sports.setBased.includes(event.sportId)) {
            return SCOREBOARD_TYPE.SET_BASED_INPLAY;
        }
        if (this.config.scoreboard.sports.periodBased.includes(event.sportId)) {
            return SCOREBOARD_TYPE.PERIOD_BASED_INPLAY;
        }
        if (event.sportId === SPORT_IDS.CRICKET) {
            return SCOREBOARD_TYPE.CRICKET;
        }
        return SCOREBOARD_TYPE.GENERIC_BASED_INPLAY;
    }

    renderScoreboardContent() {
        const type = this.getTypeOfScoreboard();
        const scoreboard = scoreboardRegistryByType[type];
        const { component: ScoreboardContentComponent, name: configName } = scoreboard;
        const componentConfig = this.config.scoreboards[configName] || {};

        return (
            <FlexPane config={this.config.content}>
                <ScoreboardContentComponent
                    config={componentConfig}
                    event={this.props.event}
                    inplayTime={this.props.inplayTime}
                    hasClockDetails={this.props.hasClockDetails}
                    showEventIndicators={this.config.showEventIndicators}
                />
            </FlexPane>
        );
    }

    getSportBackground() {
        const { event } = this.props;
        const { backgroundImages } = this.config;
        return this.appContext().getImage(
            backgroundImages[event.sportId] || backgroundImages.DEFAULT
        );
    }

    getContainerConfig() {
        const { container, showSportBackgroundImage } = this.config;
        const { backgroundImage } = container.style;
        if (!showSportBackgroundImage) {
            return container;
        }
        const sportBackground = this.getSportBackground();

        // Combine several background images if specified by config.
        // https://stackoverflow.com/questions/2504071/how-do-i-combine-a-background-image-and-css3-gradient-on-the-same-element
        const finalBackground = backgroundImage
            ? `${backgroundImage}, url(${sportBackground})`
            : `url(${sportBackground})`;

        return uiStyle.merge(container, {
            style: { backgroundImage: finalBackground },
        });
    }

    render() {
        return (
            <FlexPane config={this.getContainerConfig()} key={this.props.event.sportId}>
                {this.renderScoreboardContent()}
            </FlexPane>
        );
    }
}
